import React from 'react';
import analytics_dashboard from "../../Assets/analytics_dashboard.webp"
import {
    BarChart3, PieChart, Share2,
    Zap, ShieldCheck, 
    Target, TrendingUp, Presentation
} from 'lucide-react';
import CtaSection from '../CtaSection/CtaSection';

export default function AnalyticsPage() {
    return (
        <div className="bg-white min-h-screen text-slate-600 font-inter overflow-hidden">

            
            <section className="relative pt-20  pb-20 px-6 bg-gradient-to-b from-blue-50/10 to-white">
            
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[600px] bg-blue-100/30 blur-[120px] rounded-full" />

                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-8 shadow-sm">
                        <BarChart3 size={16} className="text-blue-600" />
                        <span className="text-xs font-bold uppercase tracking-widest text-blue-700">Intelligent Data Insights</span>
                    </div>

                    <h1 className="text-5xl md:text-8xl font-spacegrotesk font-black text-slate-900 mb-8 tracking-tighter leading-tight">
                        Smart <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-500 to-blue-400">Analytics Dashboard</span>
                    </h1>

                    <p className="max-w-3xl mx-auto text-lg md:text-xl text-slate-500 leading-relaxed mb-16">
                        Gauge the health of your digital product with real-time indicators. Obtain an instant idea of overall performance and take decisive actions.
                    </p>

                    <div className="relative max-w-3xl mx-auto group">
                        
                        <div className="relative   overflow-hidden transition-all duration-700 hover:shadow-[0_40px_80px_-15px_rgba(0,0,0,0.15)]">
                            <img
                                src={analytics_dashboard}
                                alt='Bumppy Analytics Dashboard'
                                className=" w-full"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 px-6 max-w-7xl mx-auto grid md:grid-cols-3 gap-12 border-y border-slate-100">
                {[
                    { title: "Real-time Visibility", desc: "Cloud-based access from any device including laptops, tablets, and smartphones.", num: "01" },
                    { title: "Cross-functional Unity", desc: "Start conversations right next to the charts or integrate with Slack for team study.", num: "02" },
                    { title: "Unified Big Data", desc: "Compile and arrange pertinent data from many sources into a single view.", num: "03" }
                ].map((item, idx) => (
                    <div key={idx} className="space-y-4">
                        <div className="text-blue-600 font-black text-4xl opacity-10">{item.num}</div>
                        <h3 className="text-xl font-bold text-slate-900 italic tracking-tight">{item.title}</h3>
                        <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                    </div>
                ))}
            </section>

            <section className="py-24 px-6 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

                    <div className="md:col-span-2 p-10 rounded-[3rem] bg-slate-50 border border-slate-100 group hover:bg-blue-50/50 hover:border-blue-200 transition-all duration-500">
                        <div className="h-14 w-14 rounded-2xl bg-blue-600 flex items-center justify-center mb-8 text-white shadow-lg shadow-blue-200">
                            <Zap size={28} />
                        </div>
                        <h3 className="text-3xl font-spacegrotesk font-bold text-slate-900 mb-6">Real-Time Data Reporting</h3>
                        <p className="text-slate-500 text-lg leading-relaxed mb-8">
                            Dashboards provide real-time analysis and presentation. Analysts can take immediate action on recent, accurate data, accessible from any device.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            {["Cloud Ready", "Live Updates", "Mobile Sync", "Instant Action"].map(t => (
                                <span key={t} className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-slate-600 text-xs font-bold uppercase tracking-widest shadow-sm">{t}</span>
                            ))}
                        </div>
                    </div>

                    <div className="p-10 rounded-[3rem] bg-white border border-slate-100 shadow-sm flex flex-col justify-between hover:shadow-md transition-all">
                        <div>
                            <div className="h-14 w-14 rounded-2xl bg-indigo-50 flex items-center justify-center mb-8 text-indigo-600">
                                <Share2 size={28} />
                            </div>
                            <h3 className="text-2xl font-bold font-spacegrotesk text-slate-900 mb-4 tracking-tight">Seamless Collaboration</h3>
                            <p className="text-slate-500 text-sm leading-relaxed italic">
                                "Unite everyone in your business with shared dashboards. Integrate Slack or use notebooks to offer conclusions."
                            </p>
                        </div>
                    </div>

                    <div className="p-10 rounded-[3rem] bg-indigo-600 md:col-span-1 group shadow-xl shadow-indigo-100">
                        <div className="h-14 w-14 rounded-2xl bg-white/20 flex items-center justify-center mb-8 text-white">
                            <ShieldCheck size={28} />
                        </div>
                        <h4 className="text-6xl font-black font-spacegrotesk text-white mb-4 tracking-tighter">41%</h4>
                        <p className="text-indigo-100 text-sm leading-relaxed font-medium">
                            of businesses claim their current system cannot make sense of big data. We solve this.
                        </p>
                    </div>

                   
                    <div className="md:col-span-2 relative rounded-[3rem] bg-slate-900 border border-slate-800 overflow-hidden group">
                        <div className="p-10 relative z-10 flex flex-col md:flex-row items-center gap-10">
                            <div className="space-y-6">
                                <h3 className="text-3xl font-black font-spacegrotesk text-white tracking-tighter italic">Visualize Every Metric</h3>
                                <p className="text-slate-400 leading-relaxed">Convert complex spreadsheets into intuitive bar charts, pie charts, and trend lines to increase overall effectiveness.</p>
                                <div className="flex gap-4">
                                    <BarChart3 className="text-blue-400" />
                                    <PieChart className="text-pink-400" />
                                    <TrendingUp className="text-emerald-400" />
                                </div>
                            </div>
                            <div className="w-full h-48 bg-white/5 rounded-[2rem] border border-white/10 flex items-center justify-center relative overflow-hidden">
                                
                                <TrendingUp size={60} className="text-blue-400 animate-bounce" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 bg-slate-50 relative">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
                        <div>
                            <h2 className="text-4xl md:text-6xl font-spacegrotesk font-black text-slate-900 mb-6 tracking-tight leading-none">Empowering Every <br />Stakeholder</h2>
                            <div className="h-2 w-32 bg-blue-600 rounded-full" />
                        </div>
                        <p className="text-slate-400 max-w-md font-bold uppercase tracking-[0.2em] text-xs">From Factory Managers to Customer Service Executives</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-16">
                        <div className="group p-8 rounded-[2.5rem] bg-white border border-slate-100 hover:shadow-xl transition-all duration-500">
                            <div className="shrink-0 h-16 w-16 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 mb-8 group-hover:bg-blue-600 group-hover:text-white transition-all shadow-sm">
                                <Presentation size={32} />
                            </div>
                            <div className="space-y-6">
                                <h4 className="text-3xl font-bold font-spacegrotesk text-slate-900 tracking-tighter">CEOs & Management</h4>
                                <p className="text-slate-500 text-lg leading-relaxed">
                                    Generate insights from complicated data to make strategic decisions. Monitor <span className="text-blue-600 font-bold">CLV, total revenue</span>, and new customer metrics in real-time.
                                </p>
                            </div>
                        </div>

                        <div className="group p-8 rounded-[2.5rem] bg-white border border-slate-100 hover:shadow-xl transition-all duration-500">
                            <div className="shrink-0 h-16 w-16 rounded-2xl bg-pink-50 flex items-center justify-center text-pink-600 mb-8 group-hover:bg-pink-600 group-hover:text-white transition-all shadow-sm">
                                <Target size={32} />
                            </div>
                            <div className="space-y-6">
                                <h4 className="text-3xl font-spacegrotesk font-bold text-slate-900 tracking-tighter">Marketing Teams</h4>
                                <p className="text-slate-500 text-lg leading-relaxed">
                                    Visualize data to track key marketing KPIs. Establish future goals by monitoring <span className="text-pink-600 font-bold">lead costs and conversion rates</span>.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 px-6 max-w-7xl mx-auto">
                <div className="flex flex-wrap justify-center gap-6">
                    {["Product", "Google", "Digital Marketing", "E-commerce", "Social Media", "Content"].map((tag, i) => (
                        <div key={i} className="flex items-center gap-3 px-8 py-4 rounded-full bg-white border border-slate-200 text-slate-600 font-bold tracking-tight hover:border-blue-500 hover:text-blue-600 hover:shadow-md transition-all cursor-pointer shadow-sm">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                            {tag} Dashboard
                        </div>
                    ))}
                </div>
            </section>

            <div className="px-6">
                <CtaSection />
            </div>

        </div>
    );
};